import { TableRow, TableHead as TableHeadMaterial, TableSortLabel } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { BaseTableRows, HeadCell } from './types';
import { OrderTypes } from 'shell/constants/order-types';
import { Order } from 'shell/view-models/order';
import { requestSort } from 'shell/utils/request-sort';
import { OrderFound, useOrder } from 'shell/hooks/use-order';
import { PmiHeadTableCell } from 'shell/components/shared/table-custom/head';

interface TableHeadProps<T extends BaseTableRows, K extends keyof T> {
  head: Array<HeadCell<T, K>>;
  orders: Order;
  noSort?: boolean;
  onSort: (orders: Order) => void;
  hasHorizontalScroll?: boolean;
}

interface TableHeadCellProps<T extends BaseTableRows, K extends keyof T> {
  headCell: HeadCell<T, K>;
  existOrderFunction: (head: HeadCell<T, K>) => OrderFound;
  handleRequestSort: (id: K) => void;
  noSort?: boolean;
  hasHorizontalScroll?: boolean;
}

const PmiHeadCellComponent = <T extends BaseTableRows, K extends keyof T>({
  existOrderFunction,
  headCell,
  handleRequestSort,
  noSort,
  hasHorizontalScroll,
}: TableHeadCellProps<T, K>) => {
  const { exist: existOrder, value } = useMemo(
    () => existOrderFunction(headCell),
    [headCell, existOrderFunction]
  );

  return headCell.label ? (
    <PmiHeadTableCell
      key={headCell.id.toString()}
      align="left"
      colSpan={headCell.colSpan}
      style={
        headCell.id === 'action' || headCell.overlay
          ? {
              right: 0,
              zIndex: 10,
              position: hasHorizontalScroll ? 'sticky' : 'static',
            }
          : undefined
      }
      sortDirection={!!existOrder ? value || OrderTypes.Asc : false}
    >
      {noSort ? (
        <TableSortLabel disabled active={false}>
          {headCell.label}
        </TableSortLabel>
      ) : (
        <TableSortLabel
          active={!!existOrder}
          direction={!!existOrder ? value : OrderTypes.Asc}
          onClick={() => handleRequestSort(headCell.id as K)}
        >
          {headCell.label}
        </TableSortLabel>
      )}
    </PmiHeadTableCell>
  ) : (
    <PmiHeadTableCell
      key={headCell.id.toString()}
      align="left"
      style={{
        right: 0,
        zIndex: 10,
        position: hasHorizontalScroll ? 'sticky' : 'static',
      }}
    >
      Azioni
    </PmiHeadTableCell>
  );
};

PmiHeadCellComponent.displayName = 'Pmi head cell component';

const PmiHeadCell = memo(PmiHeadCellComponent) as typeof PmiHeadCellComponent;

const TableHeadComponent = <T extends BaseTableRows, K extends keyof T>({
  head,
  onSort,
  orders,
  noSort,
  hasHorizontalScroll,
}: TableHeadProps<T, K>): React.ReactElement => {
  const existOrderFunction = useOrder<T, K>(orders);

  const handleRequestSort = useCallback(
    (property: K) => {
      const newOrders = requestSort(property, orders);
      onSort(newOrders);
    },
    [orders, onSort]
  );

  const headJsx = useMemo(() => {
    return head.map((headCell) => (
      <PmiHeadCell
        key={headCell.id.toString()}
        headCell={headCell}
        noSort={noSort}
        existOrderFunction={existOrderFunction}
        handleRequestSort={handleRequestSort}
        hasHorizontalScroll={hasHorizontalScroll}
      />
    ));
  }, [head, existOrderFunction, handleRequestSort, noSort, hasHorizontalScroll]);

  return (
    <TableHeadMaterial>
      <TableRow>{headJsx}</TableRow>
    </TableHeadMaterial>
  );
};

TableHeadComponent.displayName = 'Table Head component';

const TableHead = memo(TableHeadComponent);

export default TableHead as typeof TableHeadComponent;
